import { useState, useEffect } from 'react'
import { api, type Task, type TeamMember } from '../lib/api'
import { hapticFeedback, hapticNotification } from '../lib/telegram'

interface TeamViewProps {
  category: 'work' | 'personal'
  onRefresh: () => void
}

export function TeamView({ category, onRefresh }: TeamViewProps) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [tasksData, teamData] = await Promise.all([
        api.getTasks({ status: 'open', category }),
        api.getTeamMembers(),
      ])
      setTasks(tasksData)
      setTeamMembers(teamData)
    } catch (e) {
      console.error('Failed to fetch team data:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [category])

  const handleAssignTask = async (taskId: string, memberName: string | null) => {
    try {
      hapticFeedback('light')
      await api.updateTask(taskId, { assigned_to: memberName || undefined })
      await fetchData()
      onRefresh()
    } catch (e) {
      console.error('Failed to assign task:', e)
    }
  }

  const handleCompleteTask = async (taskId: string) => {
    try {
      hapticNotification('success')
      await api.updateTask(taskId, { completed: true })
      await fetchData()
      onRefresh()
    } catch (e) {
      console.error('Failed to complete task:', e)
    }
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return '🔴'
      case 'this_week': return '🟡'
      case 'normal': return '🔵'
      case 'backlog': return '⚪'
      default: return '⚪'
    }
  }

  const getTimeEstimateDisplay = (timeEstimate: string | null) => {
    if (!timeEstimate) return null
    const estimates: Record<string, string> = {
      '15min': '15m',
      '1hr': '1h',
      'half_day': '4h',
      'full_day': '8h'
    }
    return estimates[timeEstimate] || timeEstimate
  }

  const getDaysLeft = (dueDate: string | null) => {
    if (!dueDate) return null
    const due = new Date(dueDate)
    const now = new Date()
    const diffTime = due.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return '1d'
    if (diffDays <= 7) return `${diffDays}d`
    return `${Math.ceil(diffDays / 7)}w`
  }

  // Group tasks by assignee
  const unassignedTasks = tasks.filter(t => !t.assigned_to)
  const assignedTasksByMember = teamMembers.map(member => ({
    member,
    tasks: tasks.filter(t => t.assigned_to === member.name)
  }))

  const getTasksForMember = (memberName: string | null) => {
    if (memberName === null) return unassignedTasks
    return tasks.filter(t => t.assigned_to === memberName)
  }

  if (loading) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="spinner" />
        <p className="text-sm text-muted mt-3">Loading team data...</p>
      </div>
    )
  }

  if (category === 'personal') {
    return (
      <div className="px-4 py-8 text-center">
        <div className="text-4xl mb-3">🏠</div>
        <h2 className="text-lg font-bold mb-2">Personal Tasks</h2>
        <p className="text-sm text-muted">Team delegation is only available for work tasks.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="px-4">
        <h1 className="text-xl font-bold flex items-center gap-2 mb-4">
          👥 Team Delegation
        </h1>

        {/* Team member filter */}
        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => {
              setSelectedMember(null)
              hapticFeedback('light')
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              selectedMember === null
                ? 'bg-blue-500/20 text-blue-300'
                : 'bg-white/5 text-muted hover:bg-white/10'
            }`}
          >
            📋 All ({tasks.length})
          </button>
          <button
            onClick={() => {
              setSelectedMember('unassigned')
              hapticFeedback('light')
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              selectedMember === 'unassigned'
                ? 'bg-orange-500/20 text-orange-300'
                : 'bg-white/5 text-muted hover:bg-white/10'
            }`}
          >
            ❓ Unassigned ({unassignedTasks.length})
          </button>
          {teamMembers.map((member) => (
            <button
              key={member.id}
              onClick={() => {
                setSelectedMember(member.name)
                hapticFeedback('light')
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                selectedMember === member.name
                  ? 'bg-purple-500/20 text-purple-300'
                  : 'bg-white/5 text-muted hover:bg-white/10'
              }`}
            >
              {member.emoji} {member.name} ({tasks.filter(t => t.assigned_to === member.name).length})
            </button>
          ))}
        </div>
      </div>

      {/* Tasks */}
      <div className="px-4">
        {selectedMember === null ? (
          // Show all tasks grouped by assignment
          <div className="space-y-6">
            {/* Unassigned Tasks */}
            {unassignedTasks.length > 0 && (
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2 mb-3">
                  ❓ Unassigned Tasks ({unassignedTasks.length})
                </h2>
                <div className="space-y-3">
                  {unassignedTasks.map((task) => (
                    <TaskCard 
                      key={task.id}
                      task={task}
                      teamMembers={teamMembers}
                      onAssign={handleAssignTask}
                      onComplete={handleCompleteTask}
                      getPriorityIcon={getPriorityIcon}
                      getTimeEstimateDisplay={getTimeEstimateDisplay}
                      getDaysLeft={getDaysLeft}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tasks by Team Member */}
            {assignedTasksByMember.map(({ member, tasks: memberTasks }) => (
              memberTasks.length > 0 && (
                <div key={member.id}>
                  <h2 className="text-lg font-bold flex items-center gap-2 mb-3">
                    {member.emoji} {member.name} ({memberTasks.length})
                    <span className="text-xs text-muted font-normal">{member.role}</span>
                  </h2>
                  <div className="space-y-3">
                    {memberTasks.map((task) => (
                      <TaskCard 
                        key={task.id}
                        task={task}
                        teamMembers={teamMembers}
                        onAssign={handleAssignTask}
                        onComplete={handleCompleteTask}
                        getPriorityIcon={getPriorityIcon}
                        getTimeEstimateDisplay={getTimeEstimateDisplay}
                        getDaysLeft={getDaysLeft}
                        showAssignedTo={false}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          // Show filtered tasks
          <div>
            <h2 className="text-lg font-bold mb-3">
              {selectedMember === 'unassigned' 
                ? `❓ Unassigned Tasks (${getTasksForMember(null).length})`
                : `${teamMembers.find(m => m.name === selectedMember)?.emoji} ${selectedMember} (${getTasksForMember(selectedMember).length})`
              }
            </h2>
            <div className="space-y-3">
              {getTasksForMember(selectedMember === 'unassigned' ? null : selectedMember).map((task) => (
                <TaskCard 
                  key={task.id}
                  task={task}
                  teamMembers={teamMembers}
                  onAssign={handleAssignTask}
                  onComplete={handleCompleteTask}
                  getPriorityIcon={getPriorityIcon}
                  getTimeEstimateDisplay={getTimeEstimateDisplay}
                  getDaysLeft={getDaysLeft}
                  showAssignedTo={selectedMember === null || selectedMember === 'unassigned'}
                />
              ))}
              {getTasksForMember(selectedMember === 'unassigned' ? null : selectedMember).length === 0 && (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">🎉</div>
                  <p className="text-muted">No tasks found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface TaskCardProps {
  task: Task
  teamMembers: TeamMember[]
  onAssign: (taskId: string, memberName: string | null) => void
  onComplete: (taskId: string) => void
  getPriorityIcon: (priority: string) => string
  getTimeEstimateDisplay: (timeEstimate: string | null) => string | null
  getDaysLeft: (dueDate: string | null) => string | null
  showAssignedTo?: boolean
}

function TaskCard({ 
  task, 
  teamMembers, 
  onAssign, 
  onComplete,
  getPriorityIcon,
  getTimeEstimateDisplay,
  getDaysLeft,
  showAssignedTo = true
}: TaskCardProps) {
  const [showAssignMenu, setShowAssignMenu] = useState(false)

  return (
    <div className="glass rounded-xl p-4 border-l-4 border-blue-500">
      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        <button
          onClick={() => onComplete(task.id)}
          className="w-5 h-5 rounded border-2 border-white/30 flex items-center justify-center mt-0.5 active:scale-90 transition-all hover:border-green-400"
        >
          <div className="w-2 h-2 rounded-full bg-transparent" />
        </button>
        <div className="flex-1">
          <h3 className="font-medium text-white/90 leading-tight">{task.title}</h3>
        </div>
        <span className="text-sm">{getPriorityIcon(task.priority)}</span>
      </div>

      {/* Meta info */}
      <div className="ml-8 space-y-2">
        {task.project_name && (
          <p className="text-xs text-muted">
            {task.project_emoji} {task.project_name}
          </p>
        )}
        
        <div className="flex items-center gap-2 flex-wrap">
          {task.time_estimate && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/70">
              ⏱️ {getTimeEstimateDisplay(task.time_estimate)}
            </span>
          )}
          
          {task.due_date && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              getDaysLeft(task.due_date) === 'Overdue' ? 'bg-red-500/20 text-red-300' :
              getDaysLeft(task.due_date) === 'Today' ? 'bg-yellow-500/20 text-yellow-300' :
              'bg-blue-500/10 text-blue-300'
            }`}>
              📅 {getDaysLeft(task.due_date)}
            </span>
          )}
          
          {showAssignedTo && task.assigned_to && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/10 text-purple-300">
              {teamMembers.find(m => m.name === task.assigned_to)?.emoji || '👤'} {task.assigned_to}
            </span>
          )}
          
          <div className="relative">
            <button 
              onClick={() => setShowAssignMenu(!showAssignMenu)}
              className="px-2 py-0.5 rounded-full text-xs bg-white/5 text-white/70 hover:bg-white/10"
            >
              👤 {task.assigned_to ? 'Reassign' : 'Assign'}
            </button>
            {showAssignMenu && (
              <div className="absolute top-full left-0 mt-1 glass rounded-lg shadow-xl z-10">
                <div className="p-2 space-y-1 min-w-[140px]">
                  <button
                    onClick={() => {
                      onAssign(task.id, null)
                      setShowAssignMenu(false)
                    }}
                    className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/10 flex items-center gap-2"
                  >
                    ❌ Unassign
                  </button>
                  {teamMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => {
                        onAssign(task.id, member.name)
                        setShowAssignMenu(false)
                      }}
                      className="w-full text-left px-2 py-1 text-xs rounded hover:bg-white/10 flex items-center gap-2"
                    >
                      {member.emoji} {member.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}